import { WorkflowWorkflowType } from '@calm/evexia-web-sdk';
import createWorkflowApi from '@rhg/libs/api/create-workflow-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import { useActiveCircle } from '../use-active-circle';
import {
    workflowByDefnIdForRecipientQueryOptions,
    workflowByTypeForRecipientQueryOptions,
} from '../use-workflows';

interface StartWorkflowParameters {
    workflowDefnId: string;
    workflowType?: WorkflowWorkflowType;
}
export function useStartWorkflow() {
    const { getAccessTokenSilently } = useAuth();
    const { activeCircle } = useActiveCircle();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({ workflowDefnId }: StartWorkflowParameters) => {
            const api = createWorkflowApi(getAccessTokenSilently);
            const response = await api.v1StartWorkflow({
                circleAuthorizationToken: activeCircle!.authorizationJWT!,
                recipientId: activeCircle!.recipient.id,
                workflowDefnId,
            });
            return response;
        },
        onSuccess: (_, { workflowDefnId, workflowType }) => {
            void invalidateQueryKey(
                workflowByDefnIdForRecipientQueryOptions.queryKey({
                    workflowDefnId,
                    recipientId: activeCircle!.recipient.id,
                })
            );
            void invalidateQueryKey(
                workflowByTypeForRecipientQueryOptions.queryKey({
                    recipientId: activeCircle!.recipient.id,
                    workflowType,
                })
            );
            // Invalidate PopularWithListeners so the dynamic home section
            // reflects newly started non-clinical program guides without
            // waiting for stale-time expiry or a page refresh.
            void invalidateQueryKey([
                queryKeys.bffPopularWithListeners,
                activeCircle!.recipient.id,
            ]);
            // Invalidate recommendations so downstream hooks (e.g., useFtueMentalHealthRecommendation)
            // pick up newly created recommendations for the started workflow.
            void invalidateQueryKey([
                queryKeys.recommendations,
                activeCircle!.recipient.id,
            ]);
        },
    });
    return {
        startWorkflow: mutate,
        startWorkflowAsync: mutateAsync,
        ...otherProps,
    };
}
